import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Home as HomeIcon, X } from 'lucide-react';

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const GW = 900, GH = 500;
const GRAV = 0.58, JUMP_V = -13.5, WALK_V = 4.8;
const PW = 34, PH = 52;
const EW = 28, EH = 36;
const LEVEL_W = 5400;
const INV_DUR = 100;
const TOTAL_FIGS = 15;
const TONGUE_SPEED = 14, TONGUE_MAX = 250;
const PORTAL_X = 5260, PORTAL_Y = 360;

type GState = 'start'|'playing'|'saving'|'dead'|'win';
type TPhase = 'idle'|'out'|'in';

interface Plat  { x:number; y:number; w:number; h:number; }
interface Enemy { x:number; y:number; vx:number; alive:boolean; left:number; right:number; type:'drone'|'goo'; anim:number; }
interface Fig   { x:number; y:number; col:boolean; }

// ─── LEVEL DATA ──────────────────────────────────────────────────────────────
const PLATFORMS: Plat[] = [
  {x:0,    y:440, w:580, h:60}, {x:660,  y:440, w:520, h:60},
  {x:1270, y:440, w:420, h:60}, {x:1780, y:440, w:520, h:60},
  {x:2400, y:440, w:460, h:60}, {x:2960, y:440, w:580, h:60},
  {x:3640, y:440, w:480, h:60}, {x:4220, y:440, w:420, h:60},
  {x:4740, y:440, w:660, h:60},
  {x:200,  y:360, w:130, h:20}, {x:400,  y:300, w:110, h:20},
  {x:570,  y:348, w:120, h:20}, {x:730,  y:278, w:100, h:20},
  {x:910,  y:358, w:130, h:20}, {x:1060, y:288, w:90,  h:20},
  {x:1200, y:350, w:120, h:20}, {x:1380, y:270, w:100, h:20},
  {x:1560, y:368, w:120, h:20}, {x:1720, y:298, w:100, h:20},
  {x:1900, y:362, w:140, h:20}, {x:2090, y:282, w:90,  h:20},
  {x:2260, y:212, w:110, h:20}, {x:2460, y:342, w:130, h:20},
  {x:2640, y:272, w:100, h:20}, {x:2820, y:380, w:120, h:20},
  {x:3000, y:300, w:100, h:20}, {x:3170, y:240, w:90,  h:20},
  {x:3360, y:352, w:140, h:20}, {x:3550, y:280, w:100, h:20},
  {x:3750, y:358, w:120, h:20}, {x:3940, y:278, w:90,  h:20},
  {x:4140, y:360, w:130, h:20}, {x:4330, y:298, w:100, h:20},
  {x:4520, y:378, w:120, h:20}, {x:4720, y:298, w:100, h:20},
  {x:4920, y:342, w:120, h:20}, {x:5120, y:378, w:280, h:20},
];
const FIGS_INIT: {x:number,y:number}[] = [
  {x:265,y:330},{x:455,y:270},{x:625,y:320},{x:780,y:248},
  {x:965,y:328},{x:1105,y:258},{x:1435,y:240},{x:1775,y:268},
  {x:1975,y:332},{x:2145,y:252},{x:2315,y:182},{x:2695,y:242},
  {x:3050,y:270},{x:3220,y:210},{x:5210,y:330},
];
const ENEMIES_INIT: {x:number,y:number,l:number,r:number,t:'drone'|'goo'}[] = [
  {x:310,  y:420, l:220,  r:370,  t:'goo'},
  {x:780,  y:420, l:680,  r:880,  t:'goo'},
  {x:1060, y:268, l:1022, r:1130, t:'drone'},
  {x:1380, y:250, l:1340, r:1460, t:'goo'},
  {x:1920, y:420, l:1840, r:2060, t:'goo'},
  {x:2460, y:322, l:2420, r:2560, t:'drone'},
  {x:3360, y:332, l:3320, r:3480, t:'goo'},
  {x:4330, y:278, l:4290, r:4410, t:'drone'},
  {x:4950, y:420, l:4790, r:5060, t:'goo'},
];

// ─── DRAW: BACKGROUND (dark city/sewer) ──────────────────────────────────────
function drawBg(ctx: CanvasRenderingContext2D, camX: number, frame: number) {
  const sky = ctx.createLinearGradient(0,0,0,GH);
  sky.addColorStop(0,'#050208'); sky.addColorStop(0.6,'#0e0414'); sky.addColorStop(1,'#120820');
  ctx.fillStyle=sky; ctx.fillRect(0,0,GW,GH);

  // Rain
  ctx.strokeStyle='rgba(120,140,200,0.18)'; ctx.lineWidth=1;
  for (let i=0;i<40;i++) {
    const rx=(i*173+frame*3+camX*0.02)%GW;
    const ry=(i*97+frame*4)%(GH+20)-10;
    ctx.beginPath(); ctx.moveTo(rx,ry); ctx.lineTo(rx-2,ry+14); ctx.stroke();
  }

  // Distant city (parallax 0.06x)
  const co=camX*0.06;
  ctx.fillStyle='#0c0516';
  const bldgs=[0,380,60,340,120,360,200,300,280,340,380,280,450,320,550,260,650,300,750,270,860,310,950,280,1100,260];
  ctx.beginPath(); ctx.moveTo(0,GH);
  for (let i=0;i<bldgs.length;i+=2) {
    const bx=(bldgs[i]-co%1200+1200)%1200-100;
    ctx.lineTo(bx,bldgs[i+1]);
    ctx.lineTo(bx+80,bldgs[i+1]);
  }
  ctx.lineTo(GW,GH); ctx.closePath(); ctx.fill();

  // Windows in buildings
  ctx.fillStyle='rgba(200,50,255,0.35)';
  for (let i=0;i<18;i++) {
    const wx=((i*83+camX*0.06)%GW+GW)%GW;
    const wy=320+(i*37)%100;
    if(Math.floor((frame*0.02+i*1.3))%6>0) ctx.fillRect(wx,wy,5,7);
  }
  ctx.fillStyle='rgba(50,200,255,0.25)';
  for (let i=0;i<12;i++) {
    const wx=((i*127+40+camX*0.06)%GW+GW)%GW;
    const wy=300+(i*51)%120;
    ctx.fillRect(wx,wy,5,7);
  }

  // Toxic glow at ground level
  const tg=ctx.createLinearGradient(0,400,0,GH);
  tg.addColorStop(0,'rgba(0,0,0,0)'); tg.addColorStop(1,'rgba(60,220,60,0.06)');
  ctx.fillStyle=tg; ctx.fillRect(0,400,GW,GH-400);

  // Sewer pipes
  const po=camX*0.12;
  ctx.fillStyle='#1a1230';
  [200,600,1100,1800,2500].forEach(bx=>{
    const dx=(bx-po%2800+2800)%2800-100;
    if(dx>-50&&dx<GW+50){
      ctx.fillRect(dx,360,20,80);
      ctx.strokeStyle='#2a1a40'; ctx.lineWidth=2;
      ctx.strokeRect(dx,360,20,80);
      ctx.fillStyle='rgba(0,255,50,0.2)';
      ctx.beginPath(); ctx.arc(dx+10,365,8,0,Math.PI*2); ctx.fill();
      ctx.fillStyle='#1a1230';
    }
  });
}

// ─── DRAW: PLATFORM (sewer concrete) ─────────────────────────────────────────
function drawPlatform(ctx: CanvasRenderingContext2D, p: Plat, camX: number) {
  const dx=p.x-camX;
  if(dx+p.w<-4||dx>GW+4)return;
  ctx.fillStyle='#0f0a1a'; ctx.fillRect(dx,p.y,p.w,p.h);
  ctx.fillStyle='#1e1430'; ctx.fillRect(dx,p.y,p.w,4);
  ctx.strokeStyle='#2a1850'; ctx.lineWidth=1;
  for(let bx=0;bx<=p.w;bx+=44){ctx.beginPath();ctx.moveTo(dx+bx,p.y);ctx.lineTo(dx+bx,p.y+p.h);ctx.stroke();}
  // Toxic drip accent
  ctx.strokeStyle='rgba(80,255,80,0.15)'; ctx.lineWidth=1.5;
  ctx.strokeRect(dx+0.75,p.y+0.75,p.w-1.5,p.h-1.5);
}

// ─── DRAW: VENOM ─────────────────────────────────────────────────────────────
function drawVenom(ctx: CanvasRenderingContext2D, cx: number, feetY: number, facing: 1|-1, anim: number, inv: number, attacking: boolean) {
  if(inv>0&&Math.floor(inv/6)%2===1){ctx.globalAlpha=0.28;}
  const leg=Math.sin(anim*0.22)*12, arm=-leg;

  ctx.save(); ctx.translate(cx,feetY); ctx.scale(facing,1);

  // Legs (thick symbiote)
  ctx.strokeStyle='#0d0d14'; ctx.lineWidth=9; ctx.lineCap='round';
  ctx.beginPath(); ctx.moveTo(-6,-8); ctx.lineTo(-9+leg*0.5,5); ctx.lineTo(-11+leg*0.3,16); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(6,-8);  ctx.lineTo(9-leg*0.5,5);  ctx.lineTo(11-leg*0.3,16); ctx.stroke();
  // Feet blobs
  ctx.fillStyle='#0d0d14';
  ctx.beginPath(); ctx.ellipse(-11+leg*0.3,16,7,4,0,0,Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(11-leg*0.3,16,7,4,0,0,Math.PI*2); ctx.fill();

  // Body
  ctx.fillStyle='#0d0d14';
  ctx.beginPath(); ctx.ellipse(0,-18,17,20,0,0,Math.PI*2); ctx.fill();
  // Symbiote sheen
  const sg=ctx.createRadialGradient(-6,-24,2,-6,-24,16);
  sg.addColorStop(0,'rgba(80,0,160,0.3)'); sg.addColorStop(1,'rgba(0,0,0,0)');
  ctx.fillStyle=sg; ctx.beginPath(); ctx.ellipse(0,-18,17,20,0,0,Math.PI*2); ctx.fill();

  // Arms
  ctx.strokeStyle='#0d0d14'; ctx.lineWidth=8; ctx.lineCap='round';
  ctx.beginPath(); ctx.moveTo(-12,-22); ctx.lineTo(-18+arm*0.4,-13); ctx.lineTo(-20+arm*0.6,-5); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(12,-22);  ctx.lineTo(18-arm*0.4,-13);  ctx.lineTo(20-arm*0.6,-5);  ctx.stroke();
  // Claws
  ctx.strokeStyle='#2a2040'; ctx.lineWidth=2;
  [-20+arm*0.6,-5].forEach((_,i)=>{
    if(i===0){
      for(let c=-2;c<=2;c+=2){ctx.beginPath();ctx.moveTo(-20+arm*0.6,-5);ctx.lineTo(-20+arm*0.6+c*1.5,-5-7);ctx.stroke();}
      for(let c=-2;c<=2;c+=2){ctx.beginPath();ctx.moveTo(20-arm*0.6,-5);ctx.lineTo(20-arm*0.6+c*1.5,-5-7);ctx.stroke();}
    }
  });

  // Tendrils
  ctx.strokeStyle='#100d1a'; ctx.lineWidth=4;
  [[-15,-30,-28,-22],[-12,-12,-30,-10],[14,-28,28,-20],[12,-12,30,-8]].forEach(([sx,sy,ex,ey])=>{
    ctx.beginPath(); ctx.moveTo(sx,sy);
    ctx.quadraticCurveTo((sx+ex)/2+Math.sin(anim*0.12)*6,sy-8,ex,ey+Math.sin(anim*0.1)*4);
    ctx.stroke();
  });

  // Head (large, symbiote)
  ctx.fillStyle='#0d0d14';
  ctx.beginPath(); ctx.ellipse(0,-40,14,16,0,0,Math.PI*2); ctx.fill();
  // Head sheen
  const hg=ctx.createRadialGradient(-4,-46,1,-4,-46,10);
  hg.addColorStop(0,'rgba(100,0,200,0.25)'); hg.addColorStop(1,'rgba(0,0,0,0)');
  ctx.fillStyle=hg; ctx.beginPath(); ctx.ellipse(0,-40,14,16,0,0,Math.PI*2); ctx.fill();

  // Eyes (large white)
  ctx.fillStyle='#ffffff';
  ctx.beginPath(); ctx.ellipse(-5,-42,7,4.5,Math.PI/7,0,Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(5,-42,7,4.5,-Math.PI/7,0,Math.PI*2); ctx.fill();
  // Eye shine
  ctx.fillStyle='rgba(200,200,255,0.6)';
  ctx.beginPath(); ctx.ellipse(-6,-43,2.5,1.5,Math.PI/7,0,Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(4,-43,2.5,1.5,-Math.PI/7,0,Math.PI*2); ctx.fill();

  // Mouth (open when attacking, grin otherwise)
  if(attacking){
    ctx.fillStyle='#cc0020';
    ctx.beginPath(); ctx.arc(0,-33,10,0.1,Math.PI-0.1); ctx.fill();
    ctx.fillStyle='#ffffff';
    for(let t=-7;t<=7;t+=3.5){
      ctx.beginPath(); ctx.moveTo(t,-33); ctx.lineTo(t+1.5,-28); ctx.lineTo(t-1.5,-28); ctx.closePath(); ctx.fill();
    }
    ctx.fillStyle='#cc0020';
    for(let t=-7;t<=7;t+=3.5){
      ctx.beginPath(); ctx.moveTo(t,-38); ctx.lineTo(t+1.5,-33); ctx.lineTo(t-1.5,-33); ctx.closePath(); ctx.fill();
    }
  } else {
    ctx.strokeStyle='#cc0020'; ctx.lineWidth=2;
    ctx.beginPath(); ctx.arc(0,-33,7,0.2,Math.PI-0.2); ctx.stroke();
    ctx.fillStyle='#ffffff';
    for(let t=-5;t<=5;t+=3){ctx.fillRect(t-1,-34,2,4);}
  }

  ctx.restore(); ctx.globalAlpha=1;
}

// ─── DRAW: TONGUE ─────────────────────────────────────────────────────────────
function drawTongue(ctx: CanvasRenderingContext2D, sx: number, sy: number, facing: 1|-1, len: number, frame: number) {
  if(len<=4)return;
  ctx.save();
  // Tongue base (thick)
  ctx.strokeStyle='#cc1840'; ctx.lineWidth=6; ctx.lineCap='round'; ctx.lineJoin='round';
  ctx.beginPath(); ctx.moveTo(sx,sy);
  const segs=12;
  for(let i=1;i<=segs;i++){
    const t=i/segs;
    const x=sx+facing*len*t;
    const wave=Math.sin(t*Math.PI*3+frame*0.4)*(1-t)*10;
    ctx.lineTo(x,sy+wave);
  }
  ctx.stroke();
  // Tongue highlight
  ctx.strokeStyle='#ff4060'; ctx.lineWidth=2;
  ctx.beginPath(); ctx.moveTo(sx,sy-1);
  for(let i=1;i<=segs;i++){
    const t=i/segs;
    const x=sx+facing*len*t;
    const wave=Math.sin(t*Math.PI*3+frame*0.4)*(1-t)*10;
    ctx.lineTo(x,sy+wave-1);
  }
  ctx.stroke();
  // Forked tip
  const tipX=sx+facing*len;
  const tipY=sy+Math.sin(Math.PI*3+frame*0.4)*(0)*10;
  ctx.strokeStyle='#ff2050'; ctx.lineWidth=3;
  ctx.beginPath(); ctx.moveTo(tipX,tipY); ctx.lineTo(tipX+facing*10,tipY-6); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(tipX,tipY); ctx.lineTo(tipX+facing*10,tipY+6); ctx.stroke();
  ctx.restore();
}

// ─── DRAW: ENEMIES ────────────────────────────────────────────────────────────
function drawDrone(ctx: CanvasRenderingContext2D, x: number, y: number, anim: number) {
  const bob=Math.sin(anim*0.08)*4;
  ctx.save(); ctx.translate(x,y+bob);
  ctx.fillStyle='#1a2040';
  ctx.beginPath(); ctx.arc(0,-14,12,0,Math.PI*2); ctx.fill();
  ctx.strokeStyle='#4060c0'; ctx.lineWidth=1.5;
  ctx.beginPath(); ctx.arc(0,-14,12,0,Math.PI*2); ctx.stroke();
  // Propellers
  ctx.strokeStyle='#8090e0'; ctx.lineWidth=2;
  const pa=anim*0.3;
  ctx.beginPath(); ctx.moveTo(-12+Math.cos(pa)*4,-14+Math.sin(pa)*4); ctx.lineTo(-12-Math.cos(pa)*4,-14-Math.sin(pa)*4); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(12+Math.cos(pa+Math.PI/2)*4,-14+Math.sin(pa+Math.PI/2)*4); ctx.lineTo(12-Math.cos(pa+Math.PI/2)*4,-14-Math.sin(pa+Math.PI/2)*4); ctx.stroke();
  // Eye
  ctx.fillStyle='#ff2020';
  ctx.beginPath(); ctx.arc(0,-14,4,0,Math.PI*2); ctx.fill();
  ctx.fillStyle='rgba(255,0,0,0.2)';
  ctx.beginPath(); ctx.arc(0,-14,8,0,Math.PI*2); ctx.fill();
  ctx.restore();
}

function drawGoo(ctx: CanvasRenderingContext2D, x: number, y: number, anim: number) {
  const squish=Math.sin(anim*0.12)*2;
  ctx.save(); ctx.translate(x,y);
  ctx.fillStyle='#204010';
  ctx.beginPath(); ctx.ellipse(0,-12,14+squish,12-squish*0.5,0,0,Math.PI*2); ctx.fill();
  ctx.fillStyle='rgba(80,255,60,0.5)';
  ctx.beginPath(); ctx.ellipse(-4,-14,4,3,0,0,Math.PI*2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(4,-14,4,3,0,0,Math.PI*2); ctx.fill();
  ctx.strokeStyle='#40ff40'; ctx.lineWidth=1;
  ctx.beginPath(); ctx.arc(-4,-14,2.5,0,Math.PI*2); ctx.stroke();
  ctx.beginPath(); ctx.arc(4,-14,2.5,0,Math.PI*2); ctx.stroke();
  // drip
  const drip=Math.sin(anim*0.09)*3;
  ctx.strokeStyle='rgba(80,220,40,0.6)'; ctx.lineWidth=2;
  ctx.beginPath(); ctx.moveTo(-2,-1); ctx.lineTo(-2,4+drip); ctx.stroke();
  ctx.restore();
}

// ─── DRAW: FIGURINE ───────────────────────────────────────────────────────────
function drawFig(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number) {
  const bob=Math.sin(frame*0.07+x*0.02)*4;
  ctx.save(); ctx.translate(x,y+bob);
  const gl=ctx.createRadialGradient(0,0,1,0,0,22);
  gl.addColorStop(0,'rgba(230,185,20,0.55)'); gl.addColorStop(1,'rgba(0,0,0,0)');
  ctx.fillStyle=gl; ctx.beginPath(); ctx.arc(0,0,22,0,Math.PI*2); ctx.fill();
  ctx.fillStyle='#f0c020'; ctx.beginPath(); ctx.arc(0,-5,6.5,0,Math.PI*2); ctx.fill();
  ctx.fillRect(-4.5,2,9,10); ctx.fillRect(-7.5,12,5.5,6); ctx.fillRect(2,12,5.5,6);
  ctx.fillStyle='rgba(255,255,200,0.65)'; ctx.beginPath(); ctx.arc(-2.5,-7,2.5,0,Math.PI*2); ctx.fill();
  ctx.restore();
}

// ─── DRAW: PORTAL ─────────────────────────────────────────────────────────────
function drawPortal(ctx: CanvasRenderingContext2D, x: number, y: number, camX: number, frame: number) {
  const dx=x-camX; if(dx<-70||dx>GW+70)return;
  ctx.save(); ctx.translate(dx,y-30);
  for(let r=0;r<3;r++){
    const a=frame*0.025+r*(Math.PI*2/3);
    const pg=ctx.createRadialGradient(Math.cos(a)*9,Math.sin(a)*9,1,0,0,38);
    pg.addColorStop(0,`hsla(${300+r*40},80%,65%,0.8)`); pg.addColorStop(1,'rgba(0,0,0,0)');
    ctx.fillStyle=pg; ctx.beginPath(); ctx.arc(0,0,38,0,Math.PI*2); ctx.fill();
  }
  const cg=ctx.createRadialGradient(0,0,2,0,0,20);
  cg.addColorStop(0,'#ffffff'); cg.addColorStop(0.35,'#cc0040'); cg.addColorStop(1,'#400010');
  ctx.fillStyle=cg; ctx.beginPath(); ctx.arc(0,0,20,0,Math.PI*2); ctx.fill();
  ctx.font='bold 10px "JetBrains Mono",monospace'; ctx.textAlign='center'; ctx.fillStyle='#fff';
  ctx.shadowColor='#cc0040'; ctx.shadowBlur=8; ctx.fillText('KONIEC',0,56); ctx.shadowBlur=0;
  ctx.restore();
}

// ─── DRAW: HUD ────────────────────────────────────────────────────────────────
function drawHUD(ctx: CanvasRenderingContext2D, lives: number, figs: number, score: number, tongueReady: boolean) {
  ctx.save();
  ctx.fillStyle='rgba(5,2,14,0.85)'; ctx.fillRect(0,0,GW,40);
  ctx.strokeStyle='rgba(200,0,80,0.3)'; ctx.lineWidth=1; ctx.strokeRect(0,0,GW,40);
  ctx.font='20px serif'; ctx.textBaseline='middle';
  for(let i=0;i<3;i++){ctx.globalAlpha=i<lives?1:0.18; ctx.fillText('🖤',12+i*28,20);}
  ctx.globalAlpha=1;
  ctx.font='13px "JetBrains Mono",monospace'; ctx.fillStyle='#f0c020';
  ctx.shadowColor='#f0c020'; ctx.shadowBlur=7; ctx.textAlign='center';
  ctx.fillText(`🏆 ${figs} / ${TOTAL_FIGS} FIGUREK`,GW/2,20); ctx.shadowBlur=0;
  ctx.fillStyle=tongueReady?'#ff4060':'#660020'; ctx.textAlign='right';
  ctx.font='11px "JetBrains Mono",monospace';
  ctx.fillText(`[Z] JĘZYK ${tongueReady?'●':'○'}  WYNIK: ${score}`,GW-10,20);
  ctx.textAlign='left'; ctx.textBaseline='alphabetic'; ctx.restore();
}

// ─── DRAW: SAVING ANIMATION ───────────────────────────────────────────────────
const LAUNCH_POS=[{x:90,y:378},{x:195,y:338},{x:315,y:385},{x:435,y:358},{x:555,y:378},{x:675,y:348},{x:795,y:372},{x:145,y:292},{x:275,y:258},{x:415,y:295},{x:545,y:265},{x:695,y:285},{x:815,y:255},{x:235,y:415},{x:665,y:415}];
function drawSaving(ctx: CanvasRenderingContext2D, sf: number, fig: number, preAcct: number) {
  ctx.save();
  ctx.fillStyle=`rgba(2,0,8,${Math.min(0.92,sf*0.035)})`; ctx.fillRect(0,0,GW,GH);
  if(fig===0){ctx.restore();return;}
  const bx=GW/2,by=112,ba=Math.min(1,Math.max(0,(sf-4)*0.1));
  if(ba>0){
    ctx.globalAlpha=ba;
    const og=ctx.createRadialGradient(bx,by,5,bx,by,95);
    og.addColorStop(0,'rgba(200,0,60,0.4)'); og.addColorStop(1,'rgba(0,0,0,0)');
    ctx.fillStyle=og; ctx.beginPath(); ctx.arc(bx,by,95,0,Math.PI*2); ctx.fill();
    ctx.save(); ctx.translate(bx,by); ctx.rotate(sf*0.03);
    ctx.strokeStyle='#cc0040'; ctx.lineWidth=2.5; ctx.setLineDash([8,6]);
    ctx.beginPath(); ctx.arc(0,0,58,0,Math.PI*2); ctx.stroke();
    ctx.setLineDash([]); ctx.restore();
    const bg=ctx.createRadialGradient(bx,by,8,bx,by,52);
    bg.addColorStop(0,'#1a0004'); bg.addColorStop(1,'#0a0002');
    ctx.fillStyle=bg; ctx.beginPath(); ctx.arc(bx,by,52,0,Math.PI*2); ctx.fill();
    ctx.strokeStyle='#cc0040'; ctx.lineWidth=2; ctx.beginPath(); ctx.arc(bx,by,52,0,Math.PI*2); ctx.stroke();
    ctx.font='34px serif'; ctx.textAlign='center'; ctx.textBaseline='middle'; ctx.fillText('🖤',bx,by);
    ctx.globalAlpha=1;
  }
  const landed=Math.min(fig,Math.max(0,Math.floor((sf-18)/7)));
  if(ba>0&&fig>0){
    ctx.globalAlpha=ba;
    const pulse=1+Math.sin(sf*0.25)*0.06;
    ctx.save(); ctx.translate(bx,by+68); ctx.scale(pulse,pulse);
    ctx.font='bold 24px "JetBrains Mono",monospace'; ctx.textAlign='center'; ctx.textBaseline='top';
    ctx.fillStyle='#f0c020'; ctx.shadowColor='#f0c020'; ctx.shadowBlur=12;
    ctx.fillText(`+${landed}`,0,0); ctx.shadowBlur=0; ctx.restore();
    ctx.font='12px "JetBrains Mono",monospace'; ctx.fillStyle='rgba(240,200,60,0.7)';
    ctx.textAlign='center'; ctx.textBaseline='top';
    ctx.fillText(`łącznie: ${preAcct+landed}`,bx,by+96); ctx.globalAlpha=1;
  }
  for(let i=0;i<fig;i++){
    const launchAt=18+i*7, lp=LAUNCH_POS[i%15], ft=sf-launchAt, FLIGHT=45;
    if(ft<0){
      const bob2=Math.sin((sf+i*17)*0.12)*5;
      ctx.save(); ctx.translate(lp.x,lp.y+bob2);
      const gl2=ctx.createRadialGradient(0,0,1,0,0,16);
      gl2.addColorStop(0,'rgba(255,210,30,0.8)'); gl2.addColorStop(1,'rgba(0,0,0,0)');
      ctx.fillStyle=gl2; ctx.beginPath(); ctx.arc(0,0,16,0,Math.PI*2); ctx.fill();
      ctx.fillStyle='#f0c020'; ctx.beginPath(); ctx.arc(0,0,6,0,Math.PI*2); ctx.fill();
      ctx.restore();
    } else if(ft<=FLIGHT){
      const p=ft/FLIGHT, ep=p<0.5?2*p*p:1-Math.pow(-2*p+2,2)/2;
      const fx=lp.x+(bx-lp.x)*ep, fy=lp.y+(by-lp.y)*ep-Math.sin(p*Math.PI)*100;
      const sc=1-ep*0.55;
      ctx.save(); ctx.globalAlpha=1-ep*0.3; ctx.translate(fx,fy); ctx.scale(sc,sc);
      const gl3=ctx.createRadialGradient(0,0,1,0,0,16);
      gl3.addColorStop(0,'rgba(255,220,40,0.9)'); gl3.addColorStop(1,'rgba(0,0,0,0)');
      ctx.fillStyle=gl3; ctx.beginPath(); ctx.arc(0,0,16,0,Math.PI*2); ctx.fill();
      ctx.fillStyle='#ffcc20'; ctx.beginPath(); ctx.arc(0,0,7,0,Math.PI*2); ctx.fill();
      ctx.restore();
      if(ft>FLIGHT-5){
        const sa2=Math.max(0,1-(ft-FLIGHT+5)/5)*0.9;
        for(let s=0;s<6;s++){const a=s/6*Math.PI*2+sf*0.3,sr=12+(ft-FLIGHT+5)*9;ctx.globalAlpha=sa2;ctx.fillStyle='#ff4060';ctx.beginPath();ctx.arc(bx+Math.cos(a)*sr,by+Math.sin(a)*sr,2.5,0,Math.PI*2);ctx.fill();}
        ctx.globalAlpha=1;
      }
    }
  }
  if(sf>22){ctx.globalAlpha=Math.min(1,(sf-22)*0.08);ctx.font='bold 26px "Orbitron",sans-serif';ctx.textAlign='center';ctx.textBaseline='alphabetic';ctx.fillStyle='#cc0040';ctx.shadowColor='#cc0040';ctx.shadowBlur=14;ctx.fillText('FIGURKI SCHWYTANE JĘZYKIEM!',GW/2,232);ctx.shadowBlur=0;ctx.globalAlpha=1;}
  const allLandedAt=18+(fig-1)*7+45+12;
  if(sf>allLandedAt){ctx.globalAlpha=Math.min(1,(sf-allLandedAt)*0.1);ctx.font='bold 18px "JetBrains Mono",monospace';ctx.textAlign='center';ctx.fillStyle='#60ff90';ctx.shadowColor='#60ff90';ctx.shadowBlur=12;ctx.fillText('✓ ZAPISANO NA KONCIE',GW/2,282);ctx.shadowBlur=0;ctx.globalAlpha=1;}
  ctx.restore();
}

// ─── DRAW: OVERLAY ────────────────────────────────────────────────────────────
function drawOverlay(ctx: CanvasRenderingContext2D, type: 'start'|'dead'|'win', figs: number, score: number) {
  ctx.save(); ctx.fillStyle='rgba(2,0,8,0.88)'; ctx.fillRect(0,0,GW,GH); ctx.textAlign='center';
  if(type==='start'){
    ctx.font='bold 48px "Orbitron",sans-serif'; ctx.fillStyle='#cc0040'; ctx.shadowColor='#cc0040'; ctx.shadowBlur=22;
    ctx.fillText('VENOM',GW/2,130); ctx.shadowBlur=0;
    ctx.font='bold 22px "Rajdhani",sans-serif'; ctx.fillStyle='#f0c020';
    ctx.fillText('Atak Symbionty — Polowanie na Figurki',GW/2,175);
    ctx.font='14px "JetBrains Mono",monospace'; ctx.fillStyle='#9060a0';
    ctx.fillText('[ ← → ] ruch   [ ↑ / SPACJA ] skok   [ Z ] wystrzał języka',GW/2,230);
    ctx.fillText('Zbieraj złote figurki językiem lub zjedz wroga! (GŁOWA wroga = ofiara)',GW/2,256);
    ctx.font='bold 16px "Orbitron",sans-serif'; ctx.fillStyle='#ff4060'; ctx.shadowColor='#ff4060'; ctx.shadowBlur=10;
    if(Math.floor(Date.now()/500)%2) ctx.fillText('— NACIŚNIJ SPACJĘ LUB DOTKNIJ —',GW/2,420); ctx.shadowBlur=0;
  }
  if(type==='dead'){
    ctx.font='bold 52px "Orbitron",sans-serif'; ctx.fillStyle='#cc0040'; ctx.shadowColor='#cc0040'; ctx.shadowBlur=20;
    ctx.fillText('POCHŁONIĘTY!',GW/2,170); ctx.shadowBlur=0;
    ctx.font='20px "Rajdhani",sans-serif'; ctx.fillStyle='#f0c020';
    ctx.fillText(`Zdobyto figurek: ${figs} / ${TOTAL_FIGS}`,GW/2,238);
    ctx.font='17px "JetBrains Mono",monospace'; ctx.fillStyle='#9060a0';
    ctx.fillText(`Wynik: ${score}`,GW/2,278);
    ctx.font='bold 16px "Orbitron",sans-serif'; ctx.fillStyle='#e0c0ff'; ctx.shadowColor='#e0c0ff'; ctx.shadowBlur=7;
    if(Math.floor(Date.now()/600)%2) ctx.fillText('SPACJA / DOTKNIJ — zagraj ponownie',GW/2,400); ctx.shadowBlur=0;
  }
  if(type==='win'){
    ctx.font='bold 44px "Orbitron",sans-serif'; ctx.fillStyle='#cc0040'; ctx.shadowColor='#cc0040'; ctx.shadowBlur=20;
    ctx.fillText('SYMBIONT TRIUMFUJE!',GW/2,148); ctx.shadowBlur=0;
    ctx.font='22px "Rajdhani",sans-serif'; ctx.fillStyle='#f0c020';
    ctx.fillText(`Pożarłeś: ${figs} / ${TOTAL_FIGS} figurek`,GW/2,218);
    ctx.font='17px "JetBrains Mono",monospace'; ctx.fillStyle='#e0c0ff';
    ctx.fillText(`Wynik: ${score}`,GW/2,260);
    ctx.font='bold 16px "Orbitron",sans-serif'; ctx.fillStyle='#ff4060'; ctx.shadowColor='#ff4060'; ctx.shadowBlur=8;
    if(Math.floor(Date.now()/600)%2) ctx.fillText('SPACJA — zagraj ponownie',GW/2,400); ctx.shadowBlur=0;
  }
  ctx.textAlign='left'; ctx.restore();
}

// ─── AABB ─────────────────────────────────────────────────────────────────────
function overlaps(ax:number,ay:number,aw:number,ah:number,bx:number,by:number,bw:number,bh:number){
  return ax<bx+bw&&ax+aw>bx&&ay<by+bh&&ay+ah>by;
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function VenomGamePage() {
  const canvasRef=useRef<HTMLCanvasElement>(null);
  const afRef=useRef<number>(0);
  const {user}=useAuth();
  const navigate=useNavigate();

  const [accountFigs,setAccountFigs]=useState(0);
  const [newFigsGained,setNewFigsGained]=useState(0);
  const accountFigsRef=useRef(0);
  const gainTimerRef=useRef<ReturnType<typeof setTimeout>>();

  const mb=useRef({left:false,right:false,jump:false,tongue:false});
  const quitRef=useRef(false);
  const gameStateRef=useRef<GState>('start');
  const saveRef=useRef<(figs:number,score:number,completed:boolean)=>Promise<void>>();

  const saveToSupabase=useCallback(async(figs:number,score:number,completed:boolean)=>{
    if(!user||figs===0)return;
    try{
      await (supabase as any).from('game_sessions').insert({user_id:user.id,figurines_collected:figs,score,completed});
      const {data:prof}=await (supabase as any).from('profiles').select('game_figurines,game_best_score').eq('id',user.id).single();
      const prev=prof??{game_figurines:0,game_best_score:0};
      await (supabase as any).from('profiles').update({game_figurines:(prev.game_figurines||0)+figs,game_best_score:Math.max(prev.game_best_score||0,score)}).eq('id',user.id);
      setAccountFigs(p=>p+figs); accountFigsRef.current+=figs;
      setNewFigsGained(figs);
      if(gainTimerRef.current)clearTimeout(gainTimerRef.current);
      gainTimerRef.current=setTimeout(()=>setNewFigsGained(0),4500);
      toast.success(`+${figs} figurek pochłoniętych przez symbionty!`,{duration:3500});
    }catch(e){console.error('Błąd zapisu:',e);}
  },[user]);

  useEffect(()=>{saveRef.current=saveToSupabase;},[saveToSupabase]);

  useEffect(()=>{
    if(!user){setAccountFigs(0);accountFigsRef.current=0;return;}
    (async()=>{
      const {data}=await (supabase as any).from('profiles').select('game_figurines').eq('id',user.id).single();
      const c=data?.game_figurines??0; setAccountFigs(c); accountFigsRef.current=c;
    })();
  },[user]);

  useEffect(()=>{
    const canvas=canvasRef.current; if(!canvas)return;
    const ctx=canvas.getContext('2d')!;

    let state:GState='start';
    const setState=(s:GState)=>{state=s;gameStateRef.current=s;};

    let px=60,py=388,pvx=0,pvy=0,pg=false;
    let pf:1|-1=1,pInv=0,pAnim=0,pLives=3;
    let camX=0,fig=0,score=0,frame=0;
    let saved=false,savingFrame=0,savingFinal:'dead'|'win'='dead',preAnimAcct=0;

    // Tongue state
    let tPhase:TPhase='idle',tLen=0,tCooldown=0;
    const TCOOL=22;

    const keys=new Set<string>();
    const mkEnemies=():Enemy[]=>ENEMIES_INIT.map(e=>({x:e.x,y:e.y,left:e.l,right:e.r,type:e.t,vx:1.3,alive:true,anim:0}));
    const mkFigs=()=>FIGS_INIT.map(f=>({...f,col:false}));
    let enemies:Enemy[]=mkEnemies(), figurines:Fig[]=mkFigs();

    const doSave=(figs:number,sc:number,done:boolean)=>{if(!saved){saved=true;saveRef.current?.(figs,sc,done);}};
    const triggerSaving=(final:'dead'|'win')=>{
      if(fig===0){setState(final);return;}
      preAnimAcct=accountFigsRef.current; savingFrame=0; savingFinal=final; setState('saving');
    };
    const reset=()=>{
      setState('start'); px=60;py=388;pvx=0;pvy=0;pg=false;
      pf=1;pInv=0;pAnim=0;pLives=3;camX=0;fig=0;score=0;frame=0;
      saved=false;savingFrame=0;tPhase='idle';tLen=0;tCooldown=0;
      enemies=mkEnemies();figurines=mkFigs();
    };

    const onKeyDown=(e:KeyboardEvent)=>{
      keys.add(e.code); if(e.code==='Space')e.preventDefault();
      if(['Space','ArrowUp','KeyW'].includes(e.code)){
        if(state==='start'){setState('playing');return;}
        if(state==='dead'||state==='win'){reset();return;}
      }
    };
    const onKeyUp=(e:KeyboardEvent)=>keys.delete(e.code);
    window.addEventListener('keydown',onKeyDown);
    window.addEventListener('keyup',onKeyUp);

    const loop=()=>{
      frame++;
      const mbl=mb.current;

      // Mobile tap → start/restart
      if(mbl.jump){
        if(state==='start'){setState('playing');mbl.jump=false;}
        else if(state==='dead'||state==='win'){reset();mbl.jump=false;}
      }

      if(quitRef.current){
        quitRef.current=false;
        if(state==='playing'){doSave(fig,score,false);triggerSaving('dead');}
      }

      if(state==='saving'){
        savingFrame++;
        const dur=Math.max(130,18+(fig>0?fig:1)*7+70);
        if(savingFrame>=dur)setState(savingFinal);
      }

      if(state==='playing'){
        const goLeft =keys.has('ArrowLeft')||keys.has('KeyA')||mbl.left;
        const goRight=keys.has('ArrowRight')||keys.has('KeyD')||mbl.right;
        const doJump =keys.has('ArrowUp')||keys.has('KeyW')||keys.has('Space')||mbl.jump;
        const doTongue=(keys.has('KeyZ')||keys.has('KeyF')||mbl.tongue)&&tPhase==='idle'&&tCooldown===0;

        pvx=goLeft?-WALK_V:goRight?WALK_V:0;
        if(goLeft)pf=-1; if(goRight)pf=1;
        if(doJump&&pg){pvy=JUMP_V;pg=false;}
        if(doTongue){tPhase='out';tLen=0;}
        if(tCooldown>0)tCooldown--;

        // Tongue physics
        if(tPhase==='out'){
          tLen+=TONGUE_SPEED;
          // Tongue tip position
          const mouthX=px+(pf>0?PW:0);
          const mouthY=py+PH*0.28;
          const tipX=mouthX+pf*tLen, tipY=mouthY;

          // Hit figurine?
          for(const f of figurines){
            if(!f.col&&Math.abs(f.x-tipX)<20&&Math.abs(f.y-tipY)<20){
              f.col=true;fig++;score+=150;tPhase='in';
              if(fig===TOTAL_FIGS){score+=500;doSave(fig,score,true);triggerSaving('win');}
            }
          }
          // Hit enemy?
          for(const e of enemies){
            if(!e.alive)continue;
            const ex=e.x,ey=e.y-EH/2;
            if(Math.abs(ex-tipX)<22&&Math.abs(ey-tipY)<22){
              e.alive=false;score+=100;tPhase='in';
            }
          }
          if(tLen>=TONGUE_MAX)tPhase='in';
        }
        if(tPhase==='in'){
          tLen-=TONGUE_SPEED*1.6;
          if(tLen<=0){tLen=0;tPhase='idle';tCooldown=TCOOL;}
        }

        pvy+=GRAV;px+=pvx;py+=pvy;pg=false;
        if(pvx!==0)pAnim++;
        if(pInv>0)pInv--;
        if(px<0)px=0;
        if(px+PW>LEVEL_W)px=LEVEL_W-PW;

        for(const p of PLATFORMS){
          if(!overlaps(px,py,PW,PH,p.x,p.y,p.w,p.h))continue;
          const prevBot=py+PH-pvy;
          if(prevBot<=p.y+5&&pvy>=0){py=p.y-PH;pvy=0;pg=true;}
          else{
            const ox=Math.min(px+PW,p.x+p.w)-Math.max(px,p.x);
            const oy=Math.min(py+PH,p.y+p.h)-Math.max(py,p.y);
            if(ox<oy){px+=px<p.x?-ox:ox;}
            else if(!pg){if(py<p.y){py-=oy;pvy=0;pg=true;}else{py+=oy;if(pvy<0)pvy=0;}}
          }
        }

        if(py>GH+60){
          pLives--;pInv=INV_DUR;px=Math.max(60,camX+40);py=360;pvx=0;pvy=0;tPhase='idle';tLen=0;
          if(pLives<=0){doSave(fig,score,false);triggerSaving('dead');}
        }

        const tc=px-GW*0.38;
        camX+=(tc-camX)*0.1;
        camX=Math.max(0,Math.min(LEVEL_W-GW,camX));

        for(const e of enemies){
          if(!e.alive)continue;
          e.x+=e.vx;e.anim+=Math.abs(e.vx);
          if(e.x<=e.left){e.x=e.left;e.vx=Math.abs(e.vx);}
          if(e.x>=e.right){e.x=e.right;e.vx=-Math.abs(e.vx);}
          if(pInv>0)continue;
          const ex=e.x-EW/2,ey=e.y-EH;
          if(!overlaps(px,py,PW,PH,ex,ey,EW,EH))continue;
          // Stomp enemy from above
          if((py+PH-pvy)<=ey+14&&pvy>0){
            e.alive=false;pvy=-9;score+=80;
          } else {
            pLives--;pInv=INV_DUR;pvy=-6;pvx=pf*-3.5;
            if(pLives<=0){doSave(fig,score,false);triggerSaving('dead');}
          }
        }

        if(!saved&&overlaps(px,py,PW,PH,PORTAL_X-22,PORTAL_Y-44,44,60)){
          if(fig===TOTAL_FIGS)score+=500;
          doSave(fig,score,true);triggerSaving('win');
        }
      }

      // ── DRAW ──────────────────────────────────────────────────────────────
      drawBg(ctx,camX,frame);
      for(const p of PLATFORMS)drawPlatform(ctx,p,camX);
      drawPortal(ctx,PORTAL_X,PORTAL_Y,camX,frame);

      for(const f of figurines){
        if(f.col)continue;
        const dx=f.x-camX; if(dx<-30||dx>GW+30)continue;
        drawFig(ctx,dx,f.y,frame);
      }
      for(const e of enemies){
        if(!e.alive)continue;
        const dx=e.x-camX; if(dx<-50||dx>GW+50)continue;
        if(e.type==='drone')drawDrone(ctx,dx,e.y,e.anim);
        else drawGoo(ctx,dx,e.y,e.anim);
      }

      if(state==='playing'||state==='saving'){
        const vcx=px-camX+PW/2, vfy=py+PH;
        const attacking=tPhase!=='idle';
        // Draw tongue BEHIND Venom if facing left, in front if facing right
        if(pf>0&&tPhase!=='idle'){const mx=vcx+14,my=py+PH*0.28-camX*0+camX*0;drawTongue(ctx,mx,my,pf,tLen,frame);}
        drawVenom(ctx,vcx,vfy,pf,pAnim,pInv,attacking);
        if(pf<0&&tPhase!=='idle'){const mx=vcx-14,my=py+PH*0.28;drawTongue(ctx,mx,my,pf,tLen,frame);}
      }

      if(state==='playing')drawHUD(ctx,pLives,fig,score,tCooldown===0&&tPhase==='idle');
      if(state==='saving') drawSaving(ctx,savingFrame,fig,preAnimAcct);
      if(state==='start')  drawOverlay(ctx,'start',0,0);
      if(state==='dead')   drawOverlay(ctx,'dead',fig,score);
      if(state==='win')    drawOverlay(ctx,'win',fig,score);

      afRef.current=requestAnimationFrame(loop);
    };

    afRef.current=requestAnimationFrame(loop);
    return()=>{
      cancelAnimationFrame(afRef.current);
      window.removeEventListener('keydown',onKeyDown);
      window.removeEventListener('keyup',onKeyUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const handleQuit=()=>{
    if(gameStateRef.current==='playing'){quitRef.current=true;}else{navigate('/');}
  };
  const handleCanvasTap=(e:React.PointerEvent)=>{
    e.preventDefault(); mb.current.jump=true;
    setTimeout(()=>{mb.current.jump=false;},150);
  };

  return (
    <div className="fixed inset-0 bg-[#02000a] flex flex-col overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-[rgba(5,0,16,0.97)] border-b border-red-900/40 shrink-0 z-10 gap-3">
        <button onClick={()=>navigate('/')} className="flex items-center gap-2 text-sm text-red-300 hover:text-white transition-colors font-mono shrink-0">
          <HomeIcon className="w-4 h-4"/><span className="hidden sm:inline">Strona główna</span>
        </button>
        <span className="text-xs text-red-400/40 font-mono tracking-widest hidden md:block">VENOM: ATAK SYMBIONTY</span>
        <div className="flex items-center gap-2 shrink-0">
          {user?(
            <div className="flex items-center gap-1.5 px-3 py-1 bg-red-950/60 border border-red-700/40 rounded-lg">
              <span className="text-base leading-none">🖤</span>
              <span className="text-sm font-bold text-red-300 tabular-nums" style={{fontFamily:'JetBrains Mono,monospace'}}>{accountFigs}</span>
              {newFigsGained>0&&<span className="text-xs font-bold text-green-400 animate-bounce" style={{fontFamily:'JetBrains Mono,monospace'}}>+{newFigsGained}</span>}
            </div>
          ):(
            <span className="text-xs text-red-400/40 font-mono hidden sm:block">Zaloguj się → zbieraj figurki</span>
          )}
          <button onClick={handleQuit} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-red-400 hover:text-white bg-red-950/40 hover:bg-red-700/60 border border-red-700/50 rounded-lg transition-all" style={{fontFamily:'JetBrains Mono,monospace'}}>
            <X className="w-3.5 h-3.5"/><span className="hidden sm:inline">Zakończ</span>
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 flex items-center justify-center min-h-0 px-1 py-1">
        <canvas ref={canvasRef} width={GW} height={GH}
          onPointerDown={handleCanvasTap}
          style={{maxWidth:'100%',maxHeight:'100%',imageRendering:'pixelated',touchAction:'none',cursor:'crosshair'}}/>
      </div>

      {/* Mobile controls */}
      <div className="shrink-0 flex items-center justify-between px-4 py-2 bg-[rgba(5,0,16,0.97)] border-t border-red-900/40 select-none lg:hidden">
        <div className="flex gap-2">
          {(['left','right'] as const).map(dir=>(
            <button key={dir}
              className="w-14 h-13 bg-red-950/50 border border-red-700/50 rounded-xl text-white text-2xl font-bold active:bg-red-800/70 touch-none"
              style={{height:'52px'}}
              onPointerDown={e=>{e.currentTarget.setPointerCapture(e.pointerId);mb.current[dir]=true;}}
              onPointerUp={()=>{mb.current[dir]=false;}}
              onPointerCancel={()=>{mb.current[dir]=false;}}
              onPointerLeave={()=>{mb.current[dir]=false;}}
            >{dir==='left'?'←':'→'}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            className="w-16 h-13 bg-red-950/50 border border-red-700/50 rounded-xl text-red-200 text-lg font-bold active:bg-red-700/70 touch-none"
            style={{height:'52px',fontFamily:'JetBrains Mono,monospace',fontSize:'0.65rem'}}
            onPointerDown={e=>{e.currentTarget.setPointerCapture(e.pointerId);mb.current.tongue=true;}}
            onPointerUp={()=>{mb.current.tongue=false;}}
            onPointerCancel={()=>{mb.current.tongue=false;}}
            onPointerLeave={()=>{mb.current.tongue=false;}}
          >👅 JĘZYK</button>
          <button
            className="w-16 h-13 bg-yellow-950/50 border border-yellow-700/50 rounded-xl text-yellow-200 text-sm font-bold active:bg-yellow-700/70 touch-none"
            style={{height:'52px'}}
            onPointerDown={e=>{e.currentTarget.setPointerCapture(e.pointerId);mb.current.jump=true;}}
            onPointerUp={()=>{mb.current.jump=false;}}
            onPointerCancel={()=>{mb.current.jump=false;}}
            onPointerLeave={()=>{mb.current.jump=false;}}
          >↑ SKOK</button>
        </div>
      </div>
    </div>
  );
}
